// Shadow-DOM styles. Host sites theme the widget through the --nms-*
// custom properties on <nms-quote>; nothing here leaks out or gets
// overridden by the host page's CSS.

export const styles = /* css */ `
:host {
  --nms-navy: #0155a3;
  --nms-sky: #2a8fce;
  --nms-cyan: #00aeef;
  --nms-magenta: #ec008c;
  --nms-yellow: #fff200;
  --nms-ink: #121417;
  --nms-paper: #fbfaf6;
  --nms-stock: #f1eee6;
  --nms-rule: #d9d4c7;
  --nms-muted: #5d6169;
  --nms-danger: #c4122f;
  --nms-ok: #0a7a4b;
  --nms-radius: 3px;
  --nms-font: "Public Sans", "Segoe UI", system-ui, sans-serif;
  --nms-font-display: "Big Shoulders Text", "Barlow Condensed", "Arial Narrow", sans-serif;
  --nms-font-mono: "IBM Plex Mono", ui-monospace, "Cascadia Mono", Consolas, monospace;

  display: block;
  min-width: 0;
  max-width: 100%;
  container-type: inline-size;
  color: var(--nms-ink);
  font: 400 15px/1.45 var(--nms-font);
}
*, *::before, *::after { box-sizing: border-box; }
[hidden] { display: none !important; }

.shell {
  background: var(--nms-paper);
  border: 1px solid var(--nms-rule);
  border-radius: var(--nms-radius);
  position: relative;
  overflow: clip;
}
/* CMYK registration strip, echoing the colour chips in the NMS logo */
.shell::before {
  content: "";
  display: block;
  height: 6px;
  background: linear-gradient(90deg,
    var(--nms-cyan) 0 25%, var(--nms-magenta) 25% 50%,
    var(--nms-yellow) 50% 75%, var(--nms-ink) 75% 100%);
}

header.top {
  display: flex; align-items: end; justify-content: space-between; gap: 1rem;
  padding: 1.1rem 1.4rem .9rem;
  border-bottom: 1px solid var(--nms-rule);
}
.eyebrow {
  font: 600 .7rem/1 var(--nms-font-mono);
  letter-spacing: .14em; text-transform: uppercase; color: var(--nms-muted);
}
h2.title {
  margin: .25rem 0 0;
  font: 800 clamp(1.6rem, 4cqi, 2.3rem)/0.95 var(--nms-font-display);
  letter-spacing: .01em; text-transform: uppercase; color: var(--nms-navy);
}
.staff-badge {
  font: 700 .7rem/1 var(--nms-font-mono); letter-spacing: .1em; text-transform: uppercase;
  background: var(--nms-ink); color: var(--nms-yellow); padding: .45rem .6rem; border-radius: 2px;
}

nav.tabs {
  display: flex; gap: 0; overflow-x: auto; scrollbar-width: none;
  border-bottom: 1px solid var(--nms-rule); background: var(--nms-stock);
}
nav.tabs button {
  all: unset; cursor: pointer; white-space: nowrap;
  padding: .75rem 1.2rem; font: 700 .95rem/1 var(--nms-font-display);
  letter-spacing: .06em; text-transform: uppercase; color: var(--nms-muted);
  border-right: 1px solid var(--nms-rule); position: relative;
}
nav.tabs button[aria-selected="true"] { background: var(--nms-paper); color: var(--nms-ink); }
nav.tabs button[aria-selected="true"]::after {
  content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 3px; background: var(--nms-navy);
}
nav.tabs button:focus-visible { outline: 2px solid var(--nms-sky); outline-offset: -2px; }
/* Narrow containers: tabs wrap into a grid instead of scrolling off-screen. */
@container (max-width: 760px) {
  nav.tabs { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
  nav.tabs button { white-space: normal; text-align: center; padding: .7rem .6rem; font-size: .85rem; line-height: 1.15; border-bottom: 1px solid var(--nms-rule); }
}

.layout {
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  align-items: start;
}
@container (max-width: 760px) { .layout { grid-template-columns: 1fr; } }

.form { padding: 1.2rem 1.4rem 1.6rem; display: grid; gap: 1.4rem; }

fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
legend, .section-title {
  display: flex; align-items: center; gap: .6rem; width: 100%;
  font: 800 1.05rem/1 var(--nms-font-display); letter-spacing: .08em; text-transform: uppercase;
  margin-bottom: .7rem; padding: 0;
}
legend .num, .section-title .num {
  font: 600 .7rem/1 var(--nms-font-mono); color: var(--nms-paper); background: var(--nms-navy);
  padding: .3rem .4rem; border-radius: 2px;
}
legend::after, .section-title::after { content: ""; flex: 1; height: 1px; background: var(--nms-rule); }

label.field { display: grid; gap: .3rem; font-size: .82rem; font-weight: 600; color: var(--nms-muted); }
.row { display: grid; gap: .8rem; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }

input[type="text"], input[type="number"], input[type="email"], input[type="tel"], select, textarea {
  font: 500 .95rem/1.2 var(--nms-font); color: var(--nms-ink);
  background: #fff; border: 1px solid var(--nms-rule); border-radius: var(--nms-radius);
  padding: .6rem .65rem; width: 100%; min-width: 0;
  transition: border-color .15s, box-shadow .15s;
}
input:focus, select:focus, textarea:focus {
  outline: none; border-color: var(--nms-navy); box-shadow: 0 0 0 3px color-mix(in srgb, var(--nms-sky) 25%, transparent);
}
input[aria-invalid="true"], select[aria-invalid="true"] { border-color: var(--nms-danger); background: #fff5f6; }
input[type="number"] { font-family: var(--nms-font-mono); }

/* Radio "cards" */
.choices { display: grid; gap: .5rem; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); }
.choice {
  position: relative; display: grid; gap: .2rem; cursor: pointer;
  border: 1px solid var(--nms-rule); background: #fff; border-radius: var(--nms-radius);
  padding: .7rem .8rem .7rem 2.2rem; font-size: .86rem; color: var(--nms-muted);
  transition: border-color .15s, background .15s;
}
.choice strong { color: var(--nms-ink); font-size: .92rem; }
.choice .price { font: 600 .8rem/1 var(--nms-font-mono); color: var(--nms-navy); }
.choice input { position: absolute; left: .8rem; top: .85rem; accent-color: var(--nms-navy); margin: 0; }
.choice:has(input:checked) { border-color: var(--nms-navy); background: color-mix(in srgb, var(--nms-sky) 7%, #fff); box-shadow: inset 3px 0 0 var(--nms-navy); }
.choice:has(input:disabled) { opacity: .5; cursor: not-allowed; }

.check { display: flex; gap: .55rem; align-items: start; font-size: .88rem; cursor: pointer; }
.check input { margin-top: .2rem; accent-color: var(--nms-navy); }

/* Item cards */
.item {
  border: 1px solid var(--nms-rule); border-radius: var(--nms-radius); background: #fff;
  padding: 1rem; display: grid; gap: 1rem; position: relative;
}
.item-head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: .5rem; }
@container (max-width: 520px) {
  .form { padding: 1rem .9rem 1.2rem; }
  header.top { padding: 1rem .9rem .8rem; }
  .item { padding: .8rem; }
  .sizes { grid-template-columns: repeat(auto-fill, minmax(64px, 1fr)); }
}
.item-head .tag { font: 700 .72rem/1 var(--nms-font-mono); letter-spacing: .1em; text-transform: uppercase; color: var(--nms-navy); }

.seg { display: inline-flex; border: 1px solid var(--nms-ink); border-radius: var(--nms-radius); overflow: hidden; }
.seg button { all: unset; cursor: pointer; padding: .45rem .8rem; font: 700 .75rem/1 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase; }
.seg button[aria-pressed="true"] { background: var(--nms-ink); color: var(--nms-paper); }
.seg button:focus-visible { outline: 2px solid var(--nms-sky); outline-offset: -2px; }

.decal { border-left: 3px solid var(--nms-cyan); padding: .1rem 0 .1rem .85rem; display: grid; gap: .6rem; }
.decal:nth-child(4n+2) { border-left-color: var(--nms-magenta); }
.decal:nth-child(4n+3) { border-left-color: var(--nms-yellow); }
.decal:nth-child(4n+4) { border-left-color: var(--nms-ink); }
.decal-head { display: flex; justify-content: space-between; font: 700 .75rem/1 var(--nms-font-mono); text-transform: uppercase; letter-spacing: .08em; color: var(--nms-muted); }

.hint { font-size: .8rem; color: var(--nms-muted); margin: 0; }
.hint b { color: var(--nms-ink); }
.error { color: var(--nms-danger); font-size: .82rem; font-weight: 600; margin: 0; }
.ok { color: var(--nms-ok); font-size: .82rem; font-weight: 600; margin: 0; }

.drop {
  display: flex; gap: .8rem; align-items: center;
  border: 1.5px dashed var(--nms-rule); border-radius: var(--nms-radius); padding: .7rem;
  background: repeating-linear-gradient(-45deg, transparent 0 8px, color-mix(in srgb, var(--nms-stock) 70%, transparent) 8px 9px);
  cursor: pointer; font-size: .85rem; transition: border-color .15s;
}
.drop:hover, .drop.over { border-color: var(--nms-navy); }
.drop input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.drop .thumb {
  width: 54px; height: 54px; flex: none; border-radius: 2px; object-fit: contain;
  background: conic-gradient(#e6e3db 25%, #fff 0 50%, #e6e3db 0 75%, #fff 0) 0 0 / 12px 12px;
  border: 1px solid var(--nms-rule);
}

/* Size grid */
.sizes { display: grid; grid-template-columns: repeat(auto-fill, minmax(78px, 1fr)); gap: .4rem; }
.size { display: grid; gap: .2rem; text-align: center; font: 600 .72rem/1 var(--nms-font-mono); color: var(--nms-muted); }
.size input { text-align: center; padding: .5rem .2rem; }
.size .up { color: var(--nms-magenta); }

/* Roster grid */
.roster-wrap { overflow-x: auto; border: 1px solid var(--nms-rule); border-radius: var(--nms-radius); }
table.roster { border-collapse: collapse; width: 100%; min-width: 620px; font-size: .85rem; }
table.roster th { font: 700 .68rem/1 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase; text-align: left; padding: .55rem .4rem; background: var(--nms-stock); color: var(--nms-muted); }
table.roster td { padding: .25rem; border-top: 1px solid var(--nms-rule); }
table.roster td input, table.roster td select { padding: .4rem; font-size: .85rem; }
table.roster tr.bad td { background: #fff1f3; }
table.roster tr.bad td:first-child { box-shadow: inset 3px 0 0 var(--nms-danger); }
.banner {
  display: flex; gap: .8rem; align-items: start; justify-content: space-between;
  background: var(--nms-ink); color: var(--nms-paper); padding: .8rem 1rem; border-radius: var(--nms-radius);
  font-size: .86rem; animation: drop-in .35s cubic-bezier(.2,.9,.3,1.2);
}
.banner strong { color: var(--nms-yellow); display: block; font: 800 1rem/1.1 var(--nms-font-display); letter-spacing: .05em; text-transform: uppercase; }
.banner a, .banner button.link { color: var(--nms-cyan); }
@keyframes drop-in { from { transform: translateY(-8px); opacity: 0; } }

.btn {
  all: unset; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
  font: 700 .82rem/1 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase;
  padding: .65rem .9rem; border-radius: var(--nms-radius); border: 1px solid var(--nms-ink); color: var(--nms-ink); background: #fff;
}
.btn:hover { background: var(--nms-stock); }
.btn:focus-visible { outline: 2px solid var(--nms-sky); outline-offset: 2px; }
.btn.primary { background: var(--nms-navy); border-color: var(--nms-navy); color: #fff; }
.btn.primary:hover { background: color-mix(in srgb, var(--nms-navy) 85%, #000); }
.btn[disabled], .btn[aria-disabled="true"] { opacity: .45; cursor: not-allowed; }
.btn.ghost { border-color: transparent; background: transparent; color: var(--nms-muted); padding: .4rem .5rem; }
.btn.ghost:hover { color: var(--nms-danger); background: transparent; }
button.link { all: unset; cursor: pointer; text-decoration: underline; font-weight: 600; }
.actions { display: flex; flex-wrap: wrap; gap: .5rem; }

/* Quote ticket */
aside.ticket {
  position: sticky; top: 1rem; margin: 1.2rem 1.4rem 1.4rem 0;
  background: #fff; border: 1px solid var(--nms-ink); border-radius: var(--nms-radius);
  font-size: .86rem;
}
@container (max-width: 760px) { aside.ticket { position: static; margin: 0 1.4rem 1.4rem; } }
/* crop marks */
aside.ticket::before, aside.ticket::after {
  content: ""; position: absolute; width: 14px; height: 14px; pointer-events: none;
  border: 0 solid var(--nms-ink);
}
aside.ticket::before { top: -8px; left: -8px; border-top-width: 1px; border-left-width: 1px; }
aside.ticket::after { bottom: -8px; right: -8px; border-bottom-width: 1px; border-right-width: 1px; }
.ticket-head { padding: .85rem 1rem .7rem; border-bottom: 1px dashed var(--nms-rule); display: flex; justify-content: space-between; align-items: baseline; }
.ticket-head h3 { margin: 0; font: 800 1.15rem/1 var(--nms-font-display); letter-spacing: .08em; text-transform: uppercase; }
.ticket-body { padding: .8rem 1rem; display: grid; gap: .8rem; }
.ticket .group-title { font: 700 .72rem/1.2 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase; color: var(--nms-navy); }
.ticket dl { margin: .3rem 0 0; display: grid; grid-template-columns: 1fr auto; gap: .2rem .8rem; }
.ticket dt { color: var(--nms-muted); }
.ticket dd { margin: 0; text-align: right; font-family: var(--nms-font-mono); font-variant-numeric: tabular-nums; }
.ticket dd.neg { color: var(--nms-ok); }
.ticket .sep { border-top: 1px dashed var(--nms-rule); }
.total {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: .9rem 1rem; background: var(--nms-ink); color: var(--nms-paper);
}
.total .label { font: 700 .78rem/1 var(--nms-font-mono); letter-spacing: .1em; text-transform: uppercase; }
.total .amount { font: 800 2rem/1 var(--nms-font-display); letter-spacing: .02em; font-variant-numeric: tabular-nums; }
.total .amount.bump { animation: bump .35s ease-out; }
@keyframes bump { 40% { color: var(--nms-yellow); transform: translateY(-2px); } }
.ticket-foot { padding: .8rem 1rem 1rem; display: grid; gap: .6rem; }
.ticket-foot .note { font-size: .78rem; color: var(--nms-muted); margin: 0; }
.status { font: 700 .7rem/1 var(--nms-font-mono); letter-spacing: .08em; text-transform: uppercase; padding: .3rem .45rem; border-radius: 2px; }
.status.priced { background: color-mix(in srgb, var(--nms-ok) 14%, #fff); color: var(--nms-ok); }
.status.incomplete { background: var(--nms-stock); color: var(--nms-muted); }
.status.quote-requested { background: color-mix(in srgb, var(--nms-magenta) 12%, #fff); color: var(--nms-magenta); }
.issues { margin: 0; padding-left: 1rem; color: var(--nms-muted); font-size: .8rem; }
.warn { margin: 0; padding: .55rem .7rem; font-size: .78rem; background: color-mix(in srgb, var(--nms-yellow) 35%, #fff); border-left: 3px solid var(--nms-ink); }

/* Price-break ladder */
table.breaks { width: 100%; border-collapse: collapse; font-size: .8rem; font-variant-numeric: tabular-nums; }
table.breaks th { font: 700 .66rem/1 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase; color: var(--nms-muted); text-align: left; padding: .3rem .2rem; }
table.breaks td { padding: .35rem .2rem; border-top: 1px solid var(--nms-stock); font-family: var(--nms-font-mono); }
table.breaks td:last-child, table.breaks th:last-child { text-align: right; }
table.breaks tr.current td { background: color-mix(in srgb, var(--nms-sky) 12%, #fff); font-weight: 700; }
table.breaks tr.current td:first-child { box-shadow: inset 3px 0 0 var(--nms-navy); }
table.breaks tr[data-qty] { cursor: pointer; }
table.breaks tr[data-qty]:hover td { background: var(--nms-stock); }

.qty-picks { display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .4rem; }
.qty-picks button { all: unset; cursor: pointer; font: 600 .75rem/1 var(--nms-font-mono); padding: .4rem .55rem; border: 1px solid var(--nms-rule); border-radius: 2px; background: #fff; }
.qty-picks button[aria-pressed="true"] { border-color: var(--nms-navy); color: var(--nms-navy); box-shadow: inset 0 -2px 0 var(--nms-navy); }

dialog {
  border: 1px solid var(--nms-ink); border-radius: var(--nms-radius); padding: 0; max-width: 480px;
  font: inherit; color: inherit;
}
dialog::backdrop { background: rgb(18 20 23 / .55); }
dialog .dlg-head { background: var(--nms-yellow); padding: .8rem 1rem; font: 800 1.1rem/1.1 var(--nms-font-display); text-transform: uppercase; letter-spacing: .04em; }
dialog .dlg-body { padding: 1rem; display: grid; gap: .8rem; }

/* Running total pinned to the bottom of the screen on narrow containers,
   where the quote ticket sits below the form. */
.mini-total { display: none; }
@container (max-width: 760px) {
  .mini-total:not(:empty) {
    display: flex; justify-content: space-between; align-items: center; gap: .8rem;
    position: sticky; bottom: 0; z-index: 2; padding: .6rem 1rem;
    background: var(--nms-ink); color: var(--nms-paper); box-shadow: 0 -4px 14px rgb(18 20 23 / .18);
  }
  .mini-total .label { font: 700 .7rem/1.2 var(--nms-font-mono); letter-spacing: .08em; text-transform: uppercase; }
  .mini-total .amount { font: 800 1.45rem/1 var(--nms-font-display); font-variant-numeric: tabular-nums; }
  .mini-total button { all: unset; cursor: pointer; font: 700 .7rem/1 var(--nms-font-mono); letter-spacing: .06em; text-transform: uppercase; padding: .45rem .6rem; border: 1px solid currentColor; border-radius: 2px; }
  .mini-total.off { visibility: hidden; opacity: 0; }
  .mini-total button:focus-visible { outline: 2px solid var(--nms-yellow); outline-offset: 2px; }
}
.sent { padding: 2rem 1.4rem; text-align: center; display: grid; gap: .6rem; justify-items: center; }
.sent h3 { margin: 0; font: 800 1.6rem/1 var(--nms-font-display); text-transform: uppercase; color: var(--nms-navy); }

@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
`;
